<script setup>
/* eslint-disable no-console */
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { debounce } from '@chatwoot/utils';
import ServiceOrdersAPI from 'dashboard/api/serviceOrders';
import CeaLookupsAPI from 'dashboard/api/ceaLookups';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import MergeOrderModal from './MergeOrderModal.vue';

const { t } = useI18n();
const router = useRouter();

// State
const orders = ref([]);
const isLoading = ref(true);
const currentPage = ref(1);
const totalPages = ref(1);
const totalCount = ref(0);
const searchQuery = ref('');
const showFilters = ref(false);
const activeClassTab = ref('');
const selectedOrderIds = ref(new Set());
const showMergeModal = ref(false);
const filters = ref({
  status: '',
  order_type: '',
  created_after: '',
  created_before: '',
  request_date_after: '',
  request_date_before: '',
  aquasis_unlinked: false,
});

const unlinkedAquasisCount = ref(0);

// Order types (dynamic from CEA lookups)
const ceaOrderTypes = ref([]);
const isLoadingOrderTypes = ref(false);

// Stats from backend (counts over the full filtered set, not just current page)
const statsData = ref({});

const stats = computed(() => {
  const s = statsData.value || {};
  return {
    draftCount:        s.draft          ?? 0,
    readyCount:        s.ready          ?? 0,
    dispatchedCount:   s.dispatched     ?? 0,
    enRouteCount:      s.en_route       ?? 0,
    onSiteCount:       s.on_site        ?? 0,
    fieldCompleteCount: s.field_complete ?? 0,
    redirectedCount:   s.redirected     ?? 0,
    cancelledCount:    s.cancelled      ?? 0,
  };
});

// Check if any filters are active
const hasActiveFilters = computed(() => {
  return (
    filters.value.status ||
    filters.value.order_type ||
    filters.value.created_after ||
    filters.value.created_before ||
    filters.value.request_date_after ||
    filters.value.request_date_before ||
    filters.value.aquasis_unlinked ||
    searchQuery.value ||
    activeClassTab.value
  );
});

const buildFilterParams = () => {
  const params = {
    search: searchQuery.value,
    status: filters.value.status,
    order_type: filters.value.order_type,
    created_after: filters.value.created_after,
    created_before: filters.value.created_before,
    request_date_after: filters.value.request_date_after,
    request_date_before: filters.value.request_date_before,
  };
  if (activeClassTab.value) params.order_class = activeClassTab.value;
  if (filters.value.aquasis_unlinked) params.aquasis_unlinked = true;
  return params;
};

const fetchStats = async () => {
  try {
    const res = await ServiceOrdersAPI.reportStats();
    statsData.value = res.data?.by_status ?? {};
  } catch {
    statsData.value = {};
  }
};

const fetchOrders = async (page = 1) => {
  try {
    isLoading.value = true;
    const params = { page, ...buildFilterParams() };
    const response = await ServiceOrdersAPI.get(params);
    orders.value = response.data.payload || [];
    currentPage.value = response.data.meta?.current_page || 1;
    totalPages.value = response.data.meta?.total_pages || 1;
    totalCount.value = response.data.meta?.count || orders.value.length;
    unlinkedAquasisCount.value =
      response.data.meta?.unlinked_aquasis_count ?? 0;
  } catch (error) {
    console.error('Error fetching orders:', error);
    orders.value = [];
  } finally {
    isLoading.value = false;
  }
};

const performSearch = () => {
  fetchOrders();
  fetchStats();
};

const debouncedSearch = debounce(performSearch, 300);

const applyFilters = () => {
  fetchOrders();
  fetchStats();
};

const filterByStatus = status => {
  if (filters.value.status === status) {
    filters.value.status = '';
  } else {
    filters.value.status = status;
  }
  applyFilters();
};

const toggleAquasisUnlinkedFilter = () => {
  filters.value.aquasis_unlinked = !filters.value.aquasis_unlinked;
  applyFilters();
};

const clearFilters = () => {
  filters.value = {
    status: '',
    order_type: '',
    created_after: '',
    created_before: '',
    request_date_after: '',
    request_date_before: '',
    aquasis_unlinked: false,
  };
  searchQuery.value = '';
  activeClassTab.value = '';
  fetchOrders();
  fetchStats();
};

const toggleFilters = () => {
  showFilters.value = !showFilters.value;
};

const fetchCeaOrderTypes = async () => {
  try {
    isLoadingOrderTypes.value = true;
    const response = await CeaLookupsAPI.get('order_types');
    ceaOrderTypes.value = response.data;
  } catch (error) {
    console.error('Error fetching CEA order types:', error);
    ceaOrderTypes.value = [];
  } finally {
    isLoadingOrderTypes.value = false;
  }
};

onMounted(() => {
  fetchOrders();
  fetchStats();
  fetchCeaOrderTypes();
});

watch(searchQuery, debouncedSearch);

const getStatusColor = status => {
  const colors = {
    draft: 'bg-n-slate-9/10 text-n-slate-11',
    ready: 'bg-n-amber-9/10 text-n-amber-11',
    dispatched: 'bg-n-brand/10 text-n-blue-text',
    en_route: 'bg-violet-100 text-violet-800',
    on_site: 'bg-n-brand/10 text-n-blue-text',
    field_complete: 'bg-n-teal-9/10 text-n-teal-11',
    redirected: 'bg-orange-100 text-orange-800',
    cancelled: 'bg-n-ruby-9/10 text-n-ruby-11',
    merged: 'bg-n-slate-9/10 text-n-slate-11',
  };
  return colors[status] || 'bg-n-slate-9/10 text-n-slate-11';
};

const getStatusLabel = status => {
  const labels = {
    draft: t('ORDERS.STATUS_DRAFT'),
    ready: t('ORDERS.STATUS_READY'),
    dispatched: t('ORDERS.STATUS_DISPATCHED'),
    en_route: t('ORDERS.STATUS_EN_ROUTE'),
    on_site: t('ORDERS.STATUS_ON_SITE'),
    field_complete: t('ORDERS.STATUS_FIELD_COMPLETE'),
    redirected: t('ORDERS.STATUS_REDIRECTED'),
    cancelled: t('ORDERS.STATUS_CANCELLED'),
    merged: t('ORDERS.STATUS_MERGED'),
  };
  return labels[status] || status;
};

const getOrderTypeLabel = (type, orderTypeRecord) => {
  if (orderTypeRecord) {
    return `${orderTypeRecord.code} - ${orderTypeRecord.description}`;
  }
  return type || '-';
};

const getOrderClassBadge = orderClass => {
  if (orderClass === 'service_order') return 'OS';
  return 'OT';
};

const getOrderClassColor = orderClass => {
  if (orderClass === 'service_order') return 'bg-n-teal-9/10 text-n-teal-11';
  return 'bg-n-brand/10 text-n-blue-text';
};

const setClassTab = tab => {
  activeClassTab.value = tab;
  fetchOrders();
  fetchStats();
};

const formatDate = date => {
  if (!date) return '-';
  return new Date(date).toLocaleDateString();
};

const selectedOrderObjects = computed(() => {
  return orders.value.filter(o => selectedOrderIds.value.has(o.id));
});

const toggleOrderSelection = orderId => {
  const newSet = new Set(selectedOrderIds.value);
  if (newSet.has(orderId)) {
    newSet.delete(orderId);
  } else {
    newSet.add(orderId);
  }
  selectedOrderIds.value = newSet;
};

const toggleSelectAll = () => {
  if (selectedOrderIds.value.size === orders.value.length) {
    selectedOrderIds.value = new Set();
  } else {
    selectedOrderIds.value = new Set(orders.value.map(o => o.id));
  }
};

const onOrdersMerged = () => {
  selectedOrderIds.value = new Set();
  fetchOrders();
  fetchStats();
};

const goToOrder = orderId => {
  router.push({
    name: 'order_details',
    params: { orderId },
  });
};

const goToTicket = ticketId => {
  router.push({
    name: 'ticket_details',
    params: { ticketId },
  });
};

// Returns a windowed list of page numbers with '...' ellipsis markers.
// Always shows first, last, current, and up to 2 neighbours.
const paginationPages = computed(() => {
  const total = totalPages.value;
  const current = currentPage.value;
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);

  const pages = new Set([1, total, current]);
  if (current > 1) pages.add(current - 1);
  if (current < total) pages.add(current + 1);

  const sorted = Array.from(pages).sort((a, b) => a - b);
  const result = [];
  for (let i = 0; i < sorted.length; i++) {
    if (i > 0 && sorted[i] - sorted[i - 1] > 1) result.push('...');
    result.push(sorted[i]);
  }
  return result;
});
</script>

<template>
  <div class="flex flex-col w-full h-full min-h-0 bg-n-background">
    <!-- Header -->
    <header
      class="flex items-center justify-between p-4 border-b border-n-weak"
    >
      <div class="flex items-center gap-3">
        <h1 class="text-xl font-semibold text-n-slate-12">
          {{ $t('SIDEBAR.ORDERS') }}
        </h1>
        <span class="text-sm text-n-slate-11">
          {{ $t('ORDERS.ORDERS_COUNT', { count: totalCount }) }}
        </span>
      </div>
      <div class="flex items-center gap-2">
        <Button
          v-if="selectedOrderIds.size >= 2"
          size="sm"
          icon="i-lucide-merge"
          @click="showMergeModal = true"
        >
          {{ $t('ORDERS.MERGE_WITH_COUNT', { count: selectedOrderIds.size }) }}
        </Button>
        <Button
          v-if="hasActiveFilters"
          variant="ghost"
          size="sm"
          @click="clearFilters"
        >
          <span class="i-lucide-x mr-1" />
          {{ $t('ORDERS.CLEAR_FILTERS') }}
        </Button>
        <Button
          variant="outline"
          size="sm"
          :class="{ 'ring-2 ring-n-brand': showFilters }"
          @click="toggleFilters"
        >
          <span class="i-lucide-filter mr-1" />
          {{ $t('ORDERS.FILTERS') }}
          <span
            v-if="hasActiveFilters"
            class="ml-1 inline-block w-2 h-2 rounded-full bg-n-brand"
          />
        </Button>
      </div>
    </header>

    <!-- Order Class Tabs -->
    <div class="flex gap-1 p-2 border-b border-n-weak bg-n-solid-2">
      <button
        v-for="tab in [
          { key: '', label: $t('ORDERS.TAB_ALL') },
          { key: 'service_order', label: $t('ORDERS.TAB_SERVICE_ORDERS') },
          { key: 'work_order', label: $t('ORDERS.TAB_WORK_ORDERS') },
        ]"
        :key="tab.key"
        type="button"
        class="px-3 py-1.5 text-sm rounded-md transition-colors"
        :class="
          activeClassTab === tab.key
            ? 'bg-n-solid-3 text-n-slate-12 font-medium shadow-sm'
            : 'text-n-slate-11 hover:text-n-slate-12'
        "
        @click="setClassTab(tab.key)"
      >
        {{ tab.label }}
      </button>
    </div>

    <!-- Stats Cards -->
    <div class="flex gap-3 p-4 border-b border-n-weak overflow-x-auto">
      <div
        v-for="stat in [
          {
            key: 'draft',
            value: stats.draftCount,
            label: $t('ORDERS.STAT_DRAFT'),
          },
          {
            key: 'ready',
            value: stats.readyCount,
            label: $t('ORDERS.STAT_READY'),
          },
          {
            key: 'dispatched',
            value: stats.dispatchedCount,
            label: $t('ORDERS.STAT_DISPATCHED'),
          },
          {
            key: 'en_route',
            value: stats.enRouteCount,
            label: $t('ORDERS.STAT_EN_ROUTE'),
          },
          {
            key: 'on_site',
            value: stats.onSiteCount,
            label: $t('ORDERS.STAT_ON_SITE'),
          },
          {
            key: 'field_complete',
            value: stats.fieldCompleteCount,
            label: $t('ORDERS.STAT_FIELD_COMPLETE'),
          },
          {
            key: 'redirected',
            value: stats.redirectedCount,
            label: $t('ORDERS.STAT_REDIRECTED'),
          },
          {
            key: 'cancelled',
            value: stats.cancelledCount,
            label: $t('ORDERS.STAT_CANCELLED'),
            warning: true,
          },
        ]"
        :key="stat.key"
        class="flex flex-col items-center justify-center p-3 min-w-[100px] rounded-lg cursor-pointer transition-colors"
        :class="[
          filters.status === stat.key
            ? 'bg-n-brand/10 ring-1 ring-n-brand'
            : 'bg-n-solid-3 hover:bg-n-alpha-2',
          stat.warning ? 'text-n-ruby-11' : 'text-n-slate-12',
        ]"
        @click="filterByStatus(stat.key)"
      >
        <span class="text-2xl font-bold">{{ stat.value }}</span>
        <span class="text-xs text-n-slate-11">{{ stat.label }}</span>
      </div>
      <div
        class="flex flex-col items-center justify-center p-3 min-w-[120px] max-w-[160px] rounded-lg cursor-pointer transition-colors"
        :class="[
          filters.aquasis_unlinked
            ? 'bg-n-amber-9/15 ring-1 ring-n-amber-9'
            : 'bg-n-solid-3 hover:bg-n-alpha-2',
          'text-n-amber-11',
        ]"
        @click="toggleAquasisUnlinkedFilter"
      >
        <span class="text-2xl font-bold">{{ unlinkedAquasisCount }}</span>
        <span class="text-xs text-center text-n-slate-11 leading-tight">{{
          $t('ORDERS.STAT_AQUASIS_UNLINKED')
        }}</span>
      </div>
    </div>

    <!-- Search and Quick Filters -->
    <div
      class="flex flex-col sm:flex-row sm:items-center gap-3 p-4 border-b border-n-weak"
    >
      <div
        class="flex items-center gap-2 flex-1 min-w-[200px] px-3 py-2 rounded-lg bg-n-solid-3"
      >
        <span class="i-lucide-search text-n-slate-11 shrink-0" />
        <input
          v-model="searchQuery"
          type="text"
          :placeholder="$t('ORDERS.SEARCH_PLACEHOLDER')"
          class="w-full bg-transparent border-none outline-none text-n-slate-12 placeholder-n-slate-10"
          autocomplete="off"
        />
      </div>
      <div class="flex gap-2 overflow-x-auto pb-1 sm:pb-0 sm:overflow-visible">
        <select
          v-model="filters.status"
          class="shrink-0 px-3 py-2 rounded-lg bg-n-solid-3 border border-n-weak text-n-slate-12 text-sm"
          @change="applyFilters"
        >
          <option value="">{{ $t('ORDERS.ALL_STATUSES') }}</option>
          <option value="draft">{{ $t('ORDERS.STATUS_DRAFT') }}</option>
          <option value="ready">{{ $t('ORDERS.STATUS_READY') }}</option>
          <option value="dispatched">
            {{ $t('ORDERS.STATUS_DISPATCHED') }}
          </option>
          <option value="en_route">
            {{ $t('ORDERS.STATUS_EN_ROUTE') }}
          </option>
          <option value="on_site">
            {{ $t('ORDERS.STATUS_ON_SITE') }}
          </option>
          <option value="field_complete">
            {{ $t('ORDERS.STATUS_FIELD_COMPLETE') }}
          </option>
          <option value="redirected">
            {{ $t('ORDERS.STATUS_REDIRECTED') }}
          </option>
          <option value="cancelled">
            {{ $t('ORDERS.STATUS_CANCELLED') }}
          </option>
        </select>
        <select
          v-model="filters.order_type"
          class="shrink-0 px-3 py-2 rounded-lg bg-n-solid-3 border border-n-weak text-n-slate-12 text-sm"
          @change="applyFilters"
        >
          <option value="">{{ $t('ORDERS.ALL_TYPES') }}</option>
          <option v-for="ot in ceaOrderTypes" :key="ot.id" :value="ot.code">
            {{ ot.code }} - {{ ot.description }}
          </option>
        </select>
      </div>
    </div>

    <!-- Advanced Filters Panel -->
    <div v-if="showFilters" class="p-4 border-b border-n-weak bg-n-solid-2">
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div>
          <label class="block text-sm font-medium text-n-slate-11 mb-1">
            {{ $t('ORDERS.FILTER_CREATED_FROM') }}
          </label>
          <input
            v-model="filters.created_after"
            type="date"
            class="w-full px-3 py-2 rounded-lg bg-n-solid-3 border border-n-weak text-n-slate-12 text-sm"
            @change="applyFilters"
          />
        </div>
        <div>
          <label class="block text-sm font-medium text-n-slate-11 mb-1">
            {{ $t('ORDERS.FILTER_CREATED_TO') }}
          </label>
          <input
            v-model="filters.created_before"
            type="date"
            class="w-full px-3 py-2 rounded-lg bg-n-solid-3 border border-n-weak text-n-slate-12 text-sm"
            @change="applyFilters"
          />
        </div>
        <div>
          <label class="block text-sm font-medium text-n-slate-11 mb-1">
            {{ $t('ORDERS.FILTER_REQUEST_FROM') }}
          </label>
          <input
            v-model="filters.request_date_after"
            type="date"
            class="w-full px-3 py-2 rounded-lg bg-n-solid-3 border border-n-weak text-n-slate-12 text-sm"
            @change="applyFilters"
          />
        </div>
        <div>
          <label class="block text-sm font-medium text-n-slate-11 mb-1">
            {{ $t('ORDERS.FILTER_REQUEST_TO') }}
          </label>
          <input
            v-model="filters.request_date_before"
            type="date"
            class="w-full px-3 py-2 rounded-lg bg-n-solid-3 border border-n-weak text-n-slate-12 text-sm"
            @change="applyFilters"
          />
        </div>
      </div>
    </div>

    <!-- Content -->
    <div class="flex-1 overflow-auto p-4 bg-n-bg">
      <div v-if="isLoading" class="flex justify-center items-center py-12">
        <Spinner :size="32" />
      </div>

      <div
        v-else-if="orders.length === 0"
        class="flex flex-col items-center justify-center py-12 gap-4"
      >
        <span class="i-lucide-package text-6xl text-n-slate-10" />
        <p class="text-n-slate-11">{{ $t('ORDERS.NO_ORDERS') }}</p>
      </div>

      <table v-else class="w-full">
        <thead>
          <tr class="text-left text-sm text-n-slate-11 border-b border-n-weak">
            <th class="py-3 px-2 w-8">
              <input
                type="checkbox"
                :checked="
                  orders.length > 0 && selectedOrderIds.size === orders.length
                "
                class="accent-n-brand"
                @change="toggleSelectAll"
              />
            </th>
            <th class="py-3 px-2">{{ $t('ORDERS.ID') }}</th>
            <th class="py-3 px-2">{{ $t('ORDERS.AQUASIS_COL') }}</th>
            <th class="py-3 px-2">{{ $t('ORDERS.TICKET') }}</th>
            <th class="py-3 px-2">{{ $t('ORDERS.TYPE') }}</th>
            <th class="py-3 px-2">{{ $t('ORDERS.CONTACT') }}</th>
            <th class="py-3 px-2">{{ $t('ORDERS.REQUEST_DATE') }}</th>
            <th class="py-3 px-2">{{ $t('ORDERS.STATUS') }}</th>
            <th class="py-3 px-2">{{ $t('ORDERS.CREATED') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="order in orders"
            :key="order.id"
            class="border-b border-n-weak hover:bg-n-alpha-1 transition-colors cursor-pointer"
            @click="goToOrder(order.id)"
          >
            <td class="py-3 px-2 w-8" @click.stop>
              <input
                type="checkbox"
                :checked="selectedOrderIds.has(order.id)"
                class="accent-n-brand"
                @change="toggleOrderSelection(order.id)"
              />
            </td>
            <td class="py-3 px-2 font-mono text-sm text-n-slate-12">
              <span class="flex items-center gap-1.5">
                #{{ order.id }}
                <span
                  :class="getOrderClassColor(order.order_class)"
                  class="px-1.5 py-0.5 text-[10px] font-semibold rounded"
                >
                  {{ getOrderClassBadge(order.order_class) }}
                </span>
              </span>
            </td>
            <td class="py-3 px-2 text-sm">
              <span
                v-if="order.aquasis_order_id"
                class="font-mono text-n-brand"
              >
                {{ order.aquasis_order_id }}
              </span>
              <span
                v-else
                class="inline-flex items-center gap-1 text-n-amber-11"
              >
                <span class="i-lucide-alert-triangle text-xs" />
                <span class="text-xs">{{ $t('ORDERS.NOT_LINKED') }}</span>
              </span>
            </td>
            <td class="py-3 px-2 text-sm text-n-slate-12">
              <button
                v-if="order.ticket"
                class="font-mono text-sm text-n-brand hover:underline cursor-pointer"
                @click.stop="goToTicket(order.ticket.display_id)"
              >
                {{ order.ticket.folio }}
              </button>
              <span v-else class="text-n-slate-10">-</span>
            </td>
            <td class="py-3 px-2 text-sm text-n-slate-12">
              {{ getOrderTypeLabel(order.order_type, order.order_type_record) }}
            </td>
            <td class="py-3 px-2 text-sm text-n-slate-12">
              <span v-if="order.contact">{{ order.contact.name }}</span>
              <span v-else class="text-n-slate-10">-</span>
            </td>
            <td class="py-3 px-2 text-sm text-n-slate-11">
              {{ formatDate(order.request_date) }}
            </td>
            <td class="py-3 px-2">
              <span
                :class="getStatusColor(order.status)"
                class="px-2 py-1 text-xs font-medium rounded-full"
              >
                {{ getStatusLabel(order.status) }}
              </span>
            </td>
            <td class="py-3 px-2 text-sm text-n-slate-11">
              {{ formatDate(order.created_at) }}
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Pagination -->
      <div
        v-if="totalPages > 1"
        class="flex justify-center items-center mt-6 gap-1 flex-wrap"
      >
        <button
          class="px-2 py-1 rounded text-sm bg-n-solid-3 text-n-slate-12 hover:bg-n-solid-4 disabled:opacity-40 disabled:cursor-not-allowed"
          :disabled="currentPage === 1"
          @click="fetchOrders(1)"
        >
          «
        </button>
        <button
          class="px-2 py-1 rounded text-sm bg-n-solid-3 text-n-slate-12 hover:bg-n-solid-4 disabled:opacity-40 disabled:cursor-not-allowed"
          :disabled="currentPage === 1"
          @click="fetchOrders(currentPage - 1)"
        >
          ‹
        </button>
        <template v-for="page in paginationPages" :key="page">
          <span
            v-if="page === '...'"
            class="px-2 py-1 text-sm text-n-slate-11 select-none"
          >…</span>
          <button
            v-else
            class="px-3 py-1 rounded text-sm"
            :class="[
              page === currentPage
                ? 'bg-n-brand text-white'
                : 'bg-n-solid-3 text-n-slate-12 hover:bg-n-solid-4',
            ]"
            @click="fetchOrders(page)"
          >
            {{ page }}
          </button>
        </template>
        <button
          class="px-2 py-1 rounded text-sm bg-n-solid-3 text-n-slate-12 hover:bg-n-solid-4 disabled:opacity-40 disabled:cursor-not-allowed"
          :disabled="currentPage === totalPages"
          @click="fetchOrders(currentPage + 1)"
        >
          ›
        </button>
        <button
          class="px-2 py-1 rounded text-sm bg-n-solid-3 text-n-slate-12 hover:bg-n-solid-4 disabled:opacity-40 disabled:cursor-not-allowed"
          :disabled="currentPage === totalPages"
          @click="fetchOrders(totalPages)"
        >
          »
        </button>
      </div>
    </div>
  </div>

  <MergeOrderModal
    :show="showMergeModal"
    :selected-orders="selectedOrderObjects"
    @close="showMergeModal = false"
    @merged="onOrdersMerged"
  />
</template>
